import { JiraIssueSearchViewAri } from '@atlassian/ari/jira/issue-search-view';
import { fg } from '@atlassian/jira-feature-gating';
import type { FlagConfiguration } from '@atlassian/jira-flags/src/services/types'; // ignore-for-ENGHEALTH-17759
import { MAX_COLUMNS as MAX_AMOUNT_OF_COLUMNS } from '@atlassian/jira-native-issue-table/src/common/constants.tsx';
import { KNOWN_COLUMN_TYPES } from '@atlassian/jira-issue-table-common/src/constants.tsx';
import { CHILD_ISSUES_PAGE_SIZE } from '@atlassian/jira-issue-view-child-issues-panel/src/common/constants.tsx';
import type {
	ColumnConfigOverrideMap,
	SortOrder,
	ColumnType,
} from '@atlassian/jira-native-issue-table/src/common/types.tsx';
import messages from '../ui/messages.tsx';
import type { ColumnId } from '../ui/types.tsx';

export const JQLBuilder = ({
	issueKey,
	hideDone,
	sortColumn,
	sortDirection,
	filterSubtasks,
}: {
	issueKey: string;
	hideDone: string | null;
	sortColumn: string;
	sortDirection: SortOrder;
	filterSubtasks: boolean | null;
}) => {
	const hideDoneFlag = hideDone === 'true';
	const excludeDoneIssuesJQL = hideDoneFlag ? '& NOT statusCategory=Done' : '';

	// TODO: Remove on jira-issue-view-child-issues-panel-merge-subtasks cleanup
	const hierarchyLevelJQL =
		filterSubtasks === null ? '' : `AND hierarchyLevel ${filterSubtasks ? '=' : '!='} -1`;

	return `parent = ${issueKey} ${excludeDoneIssuesJQL} ${hierarchyLevelJQL} ORDER BY ${sortColumn} ${sortDirection}`;
};

export const onChildIssueLoadErrorFlag: FlagConfiguration = {
	type: 'error',
	title: messages.getChildIssueErrorTitle,
	description: messages.getChildIssueErrorDescription,
	isAutoDismiss: true,
	messageId:
		'issue-view-common-views.child-issues-panel.configurable-child-issues-panel.utils.on-child-issue-load-error-flag.error',
	messageType: 'transactional',
};

export const createJiraIssueSearchViewAri = ({
	cloudId,
	activationId,
}: {
	cloudId: string;
	activationId: string;
}) =>
	JiraIssueSearchViewAri.create({
		siteId: cloudId,
		activationId,
		namespaceId: 'CHILD_ISSUE_PANEL',
		viewId: 'list_default',
	}).toString();

export const createConfigurableChildIssuesPanelQueryVariables = ({
	cloudId,
	jql,
	fieldSetIds,
	issueKey,
}: {
	cloudId: string;
	jql: string;
	fieldSetIds: ColumnId[];
	issueKey: string;
}) => ({
	cloudId,
	issueSearchInput: {
		jql,
	},
	amountOfColumns: MAX_AMOUNT_OF_COLUMNS,
	namespace: 'CHILD_ISSUE_PANEL',
	options: {},
	fieldSetIds,
	shouldQueryFieldSetsById: false,
	fieldSetsInput: {
		viewInput: {
			namespace: 'CHILD_ISSUE_PANEL',
			context: {
				issueContext: {
					issueKey,
				},
			},
		},
	},
	filterId: undefined,
	fieldSetsContext: { issueContext: { issueKey } },
	first: fg('jira-issue-view-child-issues-panel-pagination')
		? // TODO: Smarter initial page size https://jplat.atlassian.net/browse/JIV-20800
			CHILD_ISSUES_PAGE_SIZE + 2
		: undefined,
	issueKey,
});

type ColumnWidth = {
	[key: ColumnType]: number;
};
const bigColumnWidth: ColumnWidth = {
	[KNOWN_COLUMN_TYPES.ISSUE_TYPE]: 54,
	[KNOWN_COLUMN_TYPES.ISSUE_KEY]: 80,
	[KNOWN_COLUMN_TYPES.STORY_POINT_ESTIMATE]: 45,
	[KNOWN_COLUMN_TYPES.NUMBER]: 45,
	[KNOWN_COLUMN_TYPES.PRIORITY]: 112,
	[KNOWN_COLUMN_TYPES.ASSIGNEE]: 132,
	[KNOWN_COLUMN_TYPES.STATUS]: 112,
	[KNOWN_COLUMN_TYPES.ISSUE_OPERATIONS]: 32,
};

const smallColumnWidth: ColumnWidth = {
	[KNOWN_COLUMN_TYPES.ISSUE_TYPE]: 40,
	[KNOWN_COLUMN_TYPES.ISSUE_KEY]: 80,
	[KNOWN_COLUMN_TYPES.STORY_POINT_ESTIMATE]: 45,
	[KNOWN_COLUMN_TYPES.NUMBER]: 45,
	[KNOWN_COLUMN_TYPES.PRIORITY]: 40,
	[KNOWN_COLUMN_TYPES.ASSIGNEE]: 40,
	[KNOWN_COLUMN_TYPES.STATUS]: 112,
	[KNOWN_COLUMN_TYPES.ISSUE_OPERATIONS]: 32,
};

export const getColumnConfigOverridesOld = (): ColumnConfigOverrideMap => {
	return {
		[KNOWN_COLUMN_TYPES.ISSUE_TYPE]: () => ({ defaultWidth: 40 }),
		[KNOWN_COLUMN_TYPES.ISSUE_KEY]: () => ({ defaultWidth: 80 }),
		[KNOWN_COLUMN_TYPES.SUMMARY]: () => ({ defaultWidth: 342 }),
		[KNOWN_COLUMN_TYPES.STORY_POINT_ESTIMATE]: () => ({ defaultWidth: 45 }),
		[KNOWN_COLUMN_TYPES.NUMBER]: () => ({ defaultWidth: 45 }),
		[KNOWN_COLUMN_TYPES.PRIORITY]: () => ({ defaultWidth: 40 }),
		[KNOWN_COLUMN_TYPES.ASSIGNEE]: () => ({ defaultWidth: 40 }),
		[KNOWN_COLUMN_TYPES.STATUS]: () => ({ defaultWidth: 112 }),
		[KNOWN_COLUMN_TYPES.ISSUE_OPERATIONS]: () => ({ defaultWidth: 32 }),
	};
};

export const getColumnConfigOverrides = (
	containerWidth: number | undefined,
	userPreferredLayout?: string | null,
): ColumnConfigOverrideMap => {
	if (!containerWidth) return getColumnConfigOverridesOld();
	const miscellaneous = 15; // padding/margin etc
	const calculatedResult: ColumnConfigOverrideMap = {};
	const defaultSummaryWidth = 342;
	// full page
	if (containerWidth > 760) {
		Object.entries(bigColumnWidth).forEach(([type, width]) => {
			calculatedResult[type] = () => ({ defaultWidth: width });
		});
		calculatedResult[KNOWN_COLUMN_TYPES.SUMMARY] = (allTypes: string[] | undefined) => {
			if (allTypes) {
				const isWideLayout = userPreferredLayout === 'WIDE';
				const minimumSummaryWidthInWideLayout = 520;
				const defaultColumnWidth = isWideLayout ? 170 : 0;
				const restWidth = allTypes.reduce((acc, current) => {
					return acc + (bigColumnWidth[current] || defaultColumnWidth);
				}, 0);
				const maxCanAllotWidth = Math.floor(
					containerWidth -
						restWidth -
						bigColumnWidth[KNOWN_COLUMN_TYPES.ISSUE_OPERATIONS] -
						miscellaneous,
				);
				const defaultWidth = isWideLayout
					? Math.max(maxCanAllotWidth, minimumSummaryWidthInWideLayout)
					: maxCanAllotWidth;
				return {
					defaultWidth,
				};
			}
			return { defaultWidth: defaultSummaryWidth };
		};

		return calculatedResult;
	}

	const minimumSummaryWidth = 100;

	Object.entries(smallColumnWidth).forEach(([type, width]) => {
		calculatedResult[type] = () => ({ defaultWidth: width });
	});

	calculatedResult[KNOWN_COLUMN_TYPES.SUMMARY] = (allTypes: string[] | undefined) => {
		if (allTypes) {
			const restWidth = allTypes.reduce((acc, current) => {
				return acc + (smallColumnWidth[current] || 0);
			}, 0);

			return {
				defaultWidth: Math.max(
					Math.floor(
						containerWidth -
							restWidth -
							smallColumnWidth[KNOWN_COLUMN_TYPES.ISSUE_OPERATIONS] -
							miscellaneous,
					),
					minimumSummaryWidth,
				),
			};
		}
		return { defaultWidth: defaultSummaryWidth };
	};

	return calculatedResult;
};
